import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  Popover,
} from '@unite-us/ui';
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableSortableHeaderColumn,
  TableRow,
} from '@unite-us/app-components';
import { EmptyTable } from 'common/tables';
import { DynamicTableRow, UnauthorizedMessage } from './components';
import getColumnStyles from './styles/getColumnStyles';
import './DynamicTable.scss';

class DynamicTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      popoverOpen: false,
    };

    this.onCellClick = this.onCellClick.bind(this);
    this.closePopover = this.closePopover.bind(this);
    this.openPopover = this.openPopover.bind(this);
  }

  onCellClick(row, column, event) {
    const { columnLabels, dataSet } = this.props;
    const colIndex = column - 1;
    const type = _.get(columnLabels, `[${colIndex}].type`);

    // If column is an action column, don't register this cell click.
    if (!['takeAction', 'download'].includes(type)) {
      event.preventDefault();
      const clickedDataObj = dataSet[row];

      if (!_.wget(clickedDataObj, 'permissions.can_view.authorization', false)) {
        if (this.state.popoverOpen) {
          this.closePopover();
        } else {
          this.openPopover(clickedDataObj, row);
        }
      } else if (_.isFunction(this.props.onCellClick)) {
        this.props.onCellClick(row, column);
      }
    }
  }

  closePopover() {
    this.setState({
      popoverOpen: false,
      popoverMessage: '',
      row: null,
    });
  }

  openPopover(clickedDataObj, row) {
    this.setState({
      popoverOpen: true,
      popoverMessage: <UnauthorizedMessage clickedDataObj={clickedDataObj} />,
      row,
    });
  }

  render() {
    const {
      caption,
      columnLabels,
      dataSet,
      emptyMessage,
      getRowProps,
      hideRowIcon,
      id,
      rowIcon,
    } = this.props;

    const { popoverMessage } = this.state;

    if (_.isEmpty(dataSet)) {
      return <EmptyTable message={emptyMessage} />;
    }

    return (
      <div className="dynamic-table" id={id}>
        <Table
          caption={caption}
          className="dynamic-table__scroll"
          onCellClick={this.onCellClick}
        >
          <TableHeader
            adjustForCheckbox={false}
            displaySelectAll={false}
          >
            <TableRow id={`${id}-header-row`}>
              {!hideRowIcon && (
                <TableHeaderColumn>
                  <span className="sr-only">Status</span>
                </TableHeaderColumn>
              )}
              {
                _.map(columnLabels, (column = {}, i) => (
                  column.sortDirection ? (
                    <TableSortableHeaderColumn
                      colName={column.sortColumn}
                      sortBy={column.sortBy || column.sortColumn}
                      setSort={column.setSort}
                      label={column.label}
                      sortDirection={column.sortDirection}
                      style={getColumnStyles(column)}
                      key={`${column.label}-${i}`}
                    >
                      {column.label}
                    </TableSortableHeaderColumn>
                  ) : (
                    <TableHeaderColumn
                      key={`${column.label}-${i}`}
                      style={getColumnStyles(column)}
                    >
                      {column.label}
                    </TableHeaderColumn>
                  )
                ))
              }
            </TableRow>
          </TableHeader>

          <TableBody
            displayRowCheckbox={false}
            showRowHover
          >
            {
              _.map(dataSet, (data, i) => {
                const rowProps = getRowProps(data, i) || {};

                return (
                  <DynamicTableRow
                    rowProps={rowProps}
                    columnLabels={columnLabels}
                    data={data}
                    hideRowIcon={hideRowIcon}
                    id={`${id}-row-${i}`}
                    key={`dynamic-table-row-${id}-${data?.id ?? i}`}
                    rowIcon={rowIcon}
                  />
                );
              })
            }
          </TableBody>
        </Table>
        {
          _.isNumber(this.state.row) && (
            <Popover
              className="dynamic-table-popover"
              hideArrow
              target={`${id}-row-${this.state.row}`}
              placement="bottom-start"
              isOpen={this.state.popoverOpen}
              toggle={this.closePopover}
              body={popoverMessage}
            />
          )
        }
      </div>
    );
  }
}

DynamicTable.propTypes = {
  caption: PropTypes.string,
  columnLabels: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
  })),
  dataSet: PropTypes.array,
  emptyMessage: PropTypes.string,
  hideRowIcon: PropTypes.bool,
  id: PropTypes.string.isRequired,
  getRowProps: PropTypes.func,
  onCellClick: PropTypes.func,
  rowIcon: PropTypes.func,
};

DynamicTable.defaultProps = {
  caption: '',
  columnLabels: [],
  dataSet: [],
  emptyMessage: '',
  getRowProps: () => ({}),
  hideRowIcon: false,
  onCellClick: _.noop,
  rowIcon: _.noop,
};

export default DynamicTable;
